import { notify } from '@affine/component';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { I18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import { requestWorkspaceSetting } from '../shared/workspace-settings-api';

export type SignatureAppearanceType =
  | 'personal_signature'
  | 'drawn_signature'
  | 'organization_seal';
export type SignatureAppearanceStatus = 'active' | 'disabled';
export type SignatureRole = 'Owner' | 'Manager' | 'Signer' | 'Viewer';
export type SignatureProviderType =
  | 'eu_dss'
  | 'open_pdf_sign'
  | 'remote_provider'
  | 'test';
export type SignatureProviderStatus = 'active' | 'disabled' | 'unavailable';
export type SignatureCertificateStatus =
  | 'active'
  | 'disabled'
  | 'expired'
  | 'revoked'
  | 'untrusted';
export type SignatureSigningProfileStatus = 'active' | 'disabled';
export type SignatureSigningProfileReadinessStatus =
  | 'not_configured'
  | 'auth_required'
  | 'identity_pending'
  | 'certificate_ready'
  | 'test_sign_passed'
  | 'expired'
  | 'revoked'
  | 'disabled'
  | 'provider_outage';
export type SignatureTestSignStatus =
  | 'not_run'
  | 'running'
  | 'passed'
  | 'failed';
export type PadesLevel = 'B' | 'T' | 'LT' | 'LTA';
export type PdfSignatureAnnotationMode = 'none' | 'flattened';
export type PdfSignatureSourceKind = 'original_pdf' | 'flattened_annotations';
export type PdfSigningOutputMode = 'download';
export type PdfSigningJobStatus =
  | 'queued'
  | 'running'
  | 'finalizing'
  | 'completed'
  | 'failed'
  | 'cancelled';
export type SignatureValidationState =
  | 'valid'
  | 'invalid'
  | 'unknown'
  | 'untrusted'
  | 'expired'
  | 'revoked'
  | 'visual_only';

export interface SignatureAppearanceInfo {
  id: string;
  workspaceId: string;
  ownerUserId: string;
  ownerDisplayName: string;
  ownerEmail: string;
  ownerAvatarUrl: string | null;
  name: string;
  type: SignatureAppearanceType;
  blobKey: string;
  mimeType: 'image/png' | 'image/jpeg' | 'image/svg+xml';
  width: number;
  height: number;
  status: SignatureAppearanceStatus;
  isDefault: boolean;
  canUse: boolean;
  canManage: boolean;
  role: SignatureRole | null;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureRoleEntry {
  id: string;
  type: 'user' | 'group';
  targetId: string;
  targetName: string;
  targetEmail?: string;
  targetAvatarUrl?: string | null;
  role: SignatureRole;
  createdAt: string;
}

export interface SignatureProviderInfo {
  id: string;
  workspaceId: string;
  name: string;
  type: SignatureProviderType;
  status: SignatureProviderStatus;
  providerCredentialId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureCertificateRefInfo {
  id: string;
  workspaceId: string;
  providerId: string;
  name: string;
  subject: string | null;
  issuer: string | null;
  fingerprint: string;
  serialNumber: string | null;
  expiresAt: string | null;
  status: SignatureCertificateStatus;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureWorkspacePolicyInfo {
  id: string;
  workspaceId: string;
  allowedProviderIds: string[];
  allowVisualOnlyExport: boolean;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureSigningProfileInfo {
  id: string;
  workspaceId: string;
  ownerUserId: string;
  providerId: string;
  certificateRefId: string;
  name: string;
  status: SignatureSigningProfileStatus;
  readinessStatus: SignatureSigningProfileReadinessStatus;
  testSignStatus: SignatureTestSignStatus;
  testSignValidationState: SignatureValidationState | null;
  failureReason: string | null;
  isDefault: boolean;
  providerName: string | null;
  certificateName: string | null;
  certificateSubject: string | null;
  certificateIssuer: string | null;
  certificateFingerprint: string | null;
  lastTestSignAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PdfSignaturePlacementSnapshot {
  id: string;
  signatureAppearanceId?: string;
  sourceId: string;
  pageIndex: number;
  rect: { x: number; y: number; width: number; height: number };
  rotation: number;
  createdBy?: string;
  createdAt?: number;
}

export interface PdfSigningJobInfo {
  id: string;
  workspaceId: string;
  requestedByUserId: string;
  sourceBlobKey: string;
  sourceHash: string;
  signingSourceBlobKey: string | null;
  signingSourceHash: string | null;
  sourceKind: PdfSignatureSourceKind;
  annotationMode: PdfSignatureAnnotationMode;
  docId: string | null;
  attachmentBlockId: string | null;
  appearanceAssetId: string | null;
  placementSnapshot: PdfSignaturePlacementSnapshot | null;
  signingProfileId: string | null;
  signingProfileName: string | null;
  providerId: string;
  providerName: string | null;
  certificateRefId: string;
  certificateSubject: string | null;
  certificateIssuer: string | null;
  certificateFingerprint: string | null;
  padesLevel: PadesLevel;
  outputMode: PdfSigningOutputMode;
  fileName: string | null;
  status: PdfSigningJobStatus;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface PdfSigningSourceSnapshotInfo {
  id: string;
  workspaceId: string;
  sourceBlobKey: string;
  sourceHash: string;
  signingSourceBlobKey: string;
  signingSourceHash: string;
  docId: string;
  attachmentBlockId: string;
  createdByUserId: string;
  expiresAt: string;
  createdAt: string;
}

export interface SignedPdfVersionInfo {
  id: string;
  workspaceId: string;
  signingJobId: string | null;
  sourceBlobKey: string;
  sourceHash: string;
  signingSourceBlobKey: string | null;
  signingSourceHash: string | null;
  sourceKind: PdfSignatureSourceKind;
  annotationMode: PdfSignatureAnnotationMode;
  signedBlobKey: string;
  signedHash: string;
  fileName: string | null;
  padesLevel: PadesLevel;
  appearanceAssetId: string | null;
  signingProfileId: string | null;
  signingProfileName: string | null;
  providerId: string | null;
  providerName: string | null;
  certificateRefId: string | null;
  certificateSubject: string | null;
  certificateIssuer: string | null;
  certificateFingerprint: string | null;
  validationState: SignatureValidationState;
  validationSummary: Record<string, unknown> | null;
  createdByUserId: string;
  createdAt: string;
}

export type PdfSignatureErrorAction =
  | 'cancel'
  | 'export'
  | 'load'
  | 'sign'
  | 'upload';
export type PdfSignatureErrorKind =
  | 'conflict'
  | 'invalid'
  | 'network'
  | 'notFound'
  | 'permission'
  | 'timeout'
  | 'tooLarge'
  | 'unknown';

export class PdfSignatureRequestError extends Error {
  readonly originalError?: unknown;
  readonly responseMessage?: string;
  readonly status?: number;

  constructor(input: {
    originalError?: unknown;
    responseMessage?: string;
    status?: number;
  }) {
    super(I18n.t('com.streamlain.pdf.signature.error.action.load'));
    this.name = 'PdfSignatureRequestError';
    this.originalError = input.originalError;
    this.responseMessage = input.responseMessage;
    this.status = input.status;
  }
}

const PDF_SIGNATURE_ERROR_ACTION_KEYS = {
  cancel: 'com.streamlain.pdf.signature.error.action.cancel',
  export: 'com.streamlain.pdf.signature.error.action.export',
  load: 'com.streamlain.pdf.signature.error.action.load',
  sign: 'com.streamlain.pdf.signature.error.action.sign',
  upload: 'com.streamlain.pdf.signature.error.action.upload',
} as const;

const PDF_SIGNATURE_ERROR_CAUSE_KEYS = {
  conflict: 'com.streamlain.pdf.signature.error.cause.conflict',
  invalid: 'com.streamlain.pdf.signature.error.cause.invalid',
  network: 'com.streamlain.pdf.signature.error.cause.network',
  notFound: 'com.streamlain.pdf.signature.error.cause.notFound',
  permission: 'com.streamlain.pdf.signature.error.cause.permission',
  timeout: 'com.streamlain.pdf.signature.error.cause.timeout',
  tooLarge: 'com.streamlain.pdf.signature.error.cause.tooLarge',
  unknown: 'com.streamlain.pdf.signature.error.cause.unknown',
} as const;

function stringifyPdfSignatureError(error: unknown): string {
  if (error instanceof PdfSignatureRequestError) {
    return [
      error.message,
      error.responseMessage,
      error.originalError instanceof Error
        ? error.originalError.message
        : String(error.originalError ?? ''),
    ]
      .join(' ')
      .toLowerCase();
  }
  if (error instanceof Error) {
    return error.message.toLowerCase();
  }
  return String(error ?? '').toLowerCase();
}

export function getPdfSignatureErrorKind(
  error: unknown
): PdfSignatureErrorKind {
  const status =
    error instanceof PdfSignatureRequestError ? error.status : undefined;
  if (status === 401 || status === 403) return 'permission';
  if (status === 404) return 'notFound';
  if (status === 413) return 'tooLarge';
  if (status === 400 || status === 415) return 'invalid';
  if (status === 409 || status === 422) return 'conflict';
  if (status === 408 || status === 504) return 'timeout';
  if (status === 502 || status === 503) return 'network';

  const message = stringifyPdfSignatureError(error);
  if (/\b(timeout|timed out|gateway timeout)\b/.test(message)) {
    return 'timeout';
  }
  if (
    /\b(failed to fetch|load failed|network|proxy|econnrefused|server unavailable|service unavailable)\b/.test(
      message
    )
  ) {
    return 'network';
  }
  if (/\b(forbidden|unauthorized|permission)\b/.test(message)) {
    return 'permission';
  }
  if (/\b(not found|missing)\b/.test(message)) {
    return 'notFound';
  }
  if (/\b(payload too large|file too large|too large)\b/.test(message)) {
    return 'tooLarge';
  }
  if (/\b(invalid|unsupported|bad request)\b/.test(message)) {
    return 'invalid';
  }
  if (/\b(conflict|not ready|locked)\b/.test(message)) {
    return 'conflict';
  }
  return 'unknown';
}

export function getPdfSignatureErrorMessage(
  error: unknown,
  action: PdfSignatureErrorAction
): string {
  const actionText = I18n.t(PDF_SIGNATURE_ERROR_ACTION_KEYS[action]);
  const causeText = I18n.t(
    PDF_SIGNATURE_ERROR_CAUSE_KEYS[getPdfSignatureErrorKind(error)]
  );
  return `${actionText} ${causeText}`;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await requestWorkspaceSetting(workspaceIdFromApiPath(url), url, init);
  } catch (error) {
    throw new PdfSignatureRequestError({ originalError: error });
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let responseMessage = text;
    try {
      const parsed = text ? (JSON.parse(text) as { message?: unknown }) : null;
      responseMessage =
        typeof parsed?.message === 'string' ? parsed.message : text;
    } catch {
      responseMessage = text;
    }
    throw new PdfSignatureRequestError({
      responseMessage,
      status: res.status,
    });
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

function workspaceIdFromApiPath(url: string): string {
  const match = /^\/api\/workspace\/([^/]+)/.exec(url);
  if (!match) {
    throw new PdfSignatureRequestError({
      responseMessage: 'PDF signature request is missing workspace scope',
    });
  }
  return decodeURIComponent(match[1]);
}

type PdfSignatureLoadState<T> = {
  data: T;
  error: unknown | null;
};

async function fetchPdfSignatureLoadJson<T>(
  url: string,
  fallback: T
): Promise<PdfSignatureLoadState<T>> {
  try {
    return {
      data: await fetchJson<T>(url),
      error: null,
    };
  } catch (error) {
    return {
      data: fallback,
      error,
    };
  }
}

export function useSignedPdfVersionForBlob(
  signedBlobKey?: string | null,
  enabled = true
) {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const apiBase = `/api/workspace/${workspaceId}/pdf-signatures`;
  const signedVersion = useSWRImmutable<
    PdfSignatureLoadState<{ versions: SignedPdfVersionInfo[] }>
  >(
    enabled && signedBlobKey
      ? `${apiBase}/signed-versions?signedBlobKey=${encodeURIComponent(
          signedBlobKey
        )}`
      : null,
    url => fetchPdfSignatureLoadJson(url, { versions: [] }),
    { suspense: false }
  );
  const signedVersionDownloadUrl = useCallback(
    (versionId: string) =>
      `${apiBase}/signed-versions/${encodeURIComponent(versionId)}/download`,
    [apiBase]
  );

  return useMemo(
    () => ({
      signedVersion: signedVersion.data?.data.versions[0] ?? null,
      error: signedVersion.data?.error ?? null,
      isLoading: Boolean(enabled && signedBlobKey && !signedVersion.data),
      refresh: signedVersion.mutate,
      signedVersionDownloadUrl,
    }),
    [
      enabled,
      signedBlobKey,
      signedVersion.data,
      signedVersion.mutate,
      signedVersionDownloadUrl,
    ]
  );
}

export function useWorkspacePdfSignatures(
  sourceBlobKey?: string | null,
  enabled = true
) {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const [isSaving, setIsSaving] = useState(false);
  const apiBase = `/api/workspace/${workspaceId}/pdf-signatures`;
  const sourceQuery = sourceBlobKey
    ? `?sourceBlobKey=${encodeURIComponent(sourceBlobKey)}`
    : '';

  const appearances = useSWRImmutable<
    PdfSignatureLoadState<{ appearances: SignatureAppearanceInfo[] }>
  >(
    enabled ? `${apiBase}/appearances` : null,
    url => fetchPdfSignatureLoadJson(url, { appearances: [] }),
    { suspense: false }
  );
  const providers = useSWRImmutable<
    PdfSignatureLoadState<{ providers: SignatureProviderInfo[] }>
  >(
    enabled ? `${apiBase}/providers` : null,
    url => fetchPdfSignatureLoadJson(url, { providers: [] }),
    { suspense: false }
  );
  const certificates = useSWRImmutable<
    PdfSignatureLoadState<{ certificates: SignatureCertificateRefInfo[] }>
  >(
    enabled ? `${apiBase}/certificates` : null,
    url => fetchPdfSignatureLoadJson(url, { certificates: [] }),
    {
      suspense: false,
    }
  );
  const profiles = useSWRImmutable<
    PdfSignatureLoadState<{ profiles: SignatureSigningProfileInfo[] }>
  >(
    enabled ? `${apiBase}/profiles` : null,
    url => fetchPdfSignatureLoadJson(url, { profiles: [] }),
    { suspense: false }
  );
  const policy = useSWRImmutable<
    PdfSignatureLoadState<{ policy: SignatureWorkspacePolicyInfo | null }>
  >(
    enabled ? `${apiBase}/policy` : null,
    url => fetchPdfSignatureLoadJson(url, { policy: null }),
    { suspense: false }
  );
  const signedVersions = useSWRImmutable<
    PdfSignatureLoadState<{ versions: SignedPdfVersionInfo[] }>
  >(
    enabled ? `${apiBase}/signed-versions${sourceQuery}` : null,
    url => fetchPdfSignatureLoadJson(url, { versions: [] }),
    { suspense: false }
  );

  const refresh = useCallback(async () => {
    await Promise.all([
      appearances.mutate(),
      providers.mutate(),
      certificates.mutate(),
      profiles.mutate(),
      policy.mutate(),
      signedVersions.mutate(),
    ]);
  }, [appearances, certificates, policy, profiles, providers, signedVersions]);

  const uploadAppearance = useCallback(
    async (input: {
      name: string;
      type: SignatureAppearanceType;
      file: File;
    }) => {
      setIsSaving(true);
      try {
        const formData = new FormData();
        formData.set('name', input.name);
        formData.set('type', input.type);
        formData.set('file', input.file);
        const result = await fetchJson<{ appearance: SignatureAppearanceInfo }>(
          `${apiBase}/appearances`,
          { method: 'POST', body: formData }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.upload.success'),
        });
        await appearances.mutate();
        return result.appearance;
      } catch (error) {
        notify.error({
          title: getPdfSignatureErrorMessage(error, 'upload'),
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const createProvider = useCallback(
    async (input: {
      name: string;
      type: SignatureProviderType;
      providerCredentialId?: string | null;
      config?: Record<string, unknown> | null;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ provider: SignatureProviderInfo }>(
          `${apiBase}/providers`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          }
        );
        notify.success({ title: 'Signing provider saved' });
        await providers.mutate();
        return result.provider;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, providers]
  );

  const updateProvider = useCallback(
    async (
      providerId: string,
      input: {
        name?: string;
        status?: SignatureProviderStatus;
        providerCredentialId?: string | null;
        config?: Record<string, unknown> | null;
      }
    ) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ provider: SignatureProviderInfo }>(
          `${apiBase}/providers/${encodeURIComponent(providerId)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          }
        );
        notify.success({ title: 'Signing provider updated' });
        await Promise.all([providers.mutate(), profiles.mutate()]);
        return result.provider;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles, providers]
  );

  const listAppearanceRoles = useCallback(
    async (appearanceId: string) => {
      const result = await fetchJson<{ roles: SignatureRoleEntry[] }>(
        `${apiBase}/appearances/${encodeURIComponent(appearanceId)}/roles`
      );
      return result.roles;
    },
    [apiBase]
  );

  const assignAppearanceRole = useCallback(
    async (input: {
      appearanceId: string;
      type: 'user' | 'group';
      targetId: string;
      role: Exclude<SignatureRole, 'Owner'>;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ roles: SignatureRoleEntry[] }>(
          `${apiBase}/appearances/${encodeURIComponent(input.appearanceId)}/roles`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: input.type,
              targetId: input.targetId,
              role: input.role,
            }),
          }
        );
        notify.success({ title: 'Signature access updated' });
        await appearances.mutate();
        return result.roles;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const removeAppearanceRole = useCallback(
    async (appearanceId: string, roleId: string) => {
      setIsSaving(true);
      try {
        await fetchJson<void>(
          `${apiBase}/appearances/${encodeURIComponent(
            appearanceId
          )}/roles/${encodeURIComponent(roleId)}`,
          { method: 'DELETE' }
        );
        notify.success({ title: 'Signature access removed' });
        await appearances.mutate();
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const setDefaultAppearance = useCallback(
    async (appearanceId: string) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ appearance: SignatureAppearanceInfo }>(
          `${apiBase}/appearances/${encodeURIComponent(appearanceId)}/default`,
          { method: 'POST' }
        );
        notify.success({ title: 'Default signature updated' });
        await appearances.mutate();
        return result.appearance;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const updateAppearance = useCallback(
    async (
      appearanceId: string,
      input: {
        name?: string;
        status?: SignatureAppearanceStatus;
      }
    ) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ appearance: SignatureAppearanceInfo }>(
          `${apiBase}/appearances/${encodeURIComponent(appearanceId)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.settings.saved'),
        });
        await appearances.mutate();
        return result.appearance;
      } catch (error) {
        notify.error({
          title: getPdfSignatureErrorMessage(error, 'upload'),
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const deleteAppearance = useCallback(
    async (appearanceId: string) => {
      setIsSaving(true);
      try {
        await fetchJson<void>(
          `${apiBase}/appearances/${encodeURIComponent(appearanceId)}`,
          { method: 'DELETE' }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.settings.deleted'),
        });
        await appearances.mutate();
      } catch (error) {
        notify.error({
          title: getPdfSignatureErrorMessage(error, 'upload'),
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const createCertificate = useCallback(
    async (input: {
      providerId: string;
      name: string;
      subject?: string | null;
      issuer?: string | null;
      fingerprint: string;
      serialNumber?: string | null;
      expiresAt?: string | null;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          certificate: SignatureCertificateRefInfo;
        }>(`${apiBase}/certificates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        notify.success({ title: 'Certificate saved' });
        await certificates.mutate();
        return result.certificate;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, certificates]
  );

  const updateCertificate = useCallback(
    async (
      certificateRefId: string,
      input: {
        name?: string;
        subject?: string | null;
        issuer?: string | null;
        status?: SignatureCertificateStatus;
        expiresAt?: string | null;
      }
    ) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          certificate: SignatureCertificateRefInfo;
        }>(`${apiBase}/certificates/${encodeURIComponent(certificateRefId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        notify.success({ title: 'Certificate updated' });
        await Promise.all([certificates.mutate(), profiles.mutate()]);
        return result.certificate;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, certificates, profiles]
  );

  const createSigningProfile = useCallback(
    async (input: {
      name: string;
      providerId: string;
      certificateRefId: string;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          profile: SignatureSigningProfileInfo;
        }>(`${apiBase}/profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        notify.success({ title: 'Signing profile saved' });
        await profiles.mutate();
        return result.profile;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles]
  );

  const updateSigningProfile = useCallback(
    async (
      profileId: string,
      input: {
        name?: string;
        providerId?: string;
        certificateRefId?: string;
        status?: SignatureSigningProfileStatus;
      }
    ) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          profile: SignatureSigningProfileInfo;
        }>(`${apiBase}/profiles/${encodeURIComponent(profileId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        notify.success({ title: 'Signing profile updated' });
        await profiles.mutate();
        return result.profile;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles]
  );

  const deleteSigningProfile = useCallback(
    async (profileId: string) => {
      setIsSaving(true);
      try {
        await fetchJson<void>(
          `${apiBase}/profiles/${encodeURIComponent(profileId)}`,
          { method: 'DELETE' }
        );
        notify.success({ title: 'Signing profile disabled' });
        await profiles.mutate();
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles]
  );

  const setDefaultSigningProfile = useCallback(
    async (profileId: string) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          profile: SignatureSigningProfileInfo;
        }>(`${apiBase}/profiles/${encodeURIComponent(profileId)}/default`, {
          method: 'POST',
        });
        notify.success({ title: 'Default signing profile updated' });
        await profiles.mutate();
        return result.profile;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles]
  );

  const testSigningProfile = useCallback(
    async (profileId: string) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          profile: SignatureSigningProfileInfo;
        }>(`${apiBase}/profiles/${encodeURIComponent(profileId)}/test-sign`, {
          method: 'POST',
        });
        notify.success({ title: 'Signing profile tested' });
        await profiles.mutate();
        return result.profile;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles]
  );

  const updatePolicy = useCallback(
    async (input: {
      allowedProviderIds?: string[];
      allowVisualOnlyExport?: boolean;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          policy: SignatureWorkspacePolicyInfo;
        }>(`${apiBase}/policy`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        notify.success({ title: 'Signature policy saved' });
        await policy.mutate();
        return result.policy;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, policy]
  );

  const recordVisualExport = useCallback(
    async (input: {
      sourceBlobKey: string;
      docId?: string | null;
      attachmentBlockId?: string | null;
      placementCount: number;
      output: 'print' | 'export';
    }) => {
      await fetchJson<void>(`${apiBase}/visual-exports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
    },
    [apiBase]
  );

  const createSigningSourceSnapshot = useCallback(
    async (input: {
      sourceBlobKey: string;
      signingSourceBlobKey: string;
      docId: string;
      attachmentBlockId: string;
    }) => {
      const result = await fetchJson<{
        snapshot: PdfSigningSourceSnapshotInfo;
      }>(`${apiBase}/signing-source-snapshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return result.snapshot;
    },
    [apiBase]
  );

  const requestSigning = useCallback(
    async (input: {
      sourceBlobKey: string;
      signingSourceBlobKey?: string | null;
      signingSourceSnapshotId?: string | null;
      includeAnnotations?: boolean;
      docId?: string | null;
      attachmentBlockId?: string | null;
      appearanceAssetId?: string | null;
      placementSnapshot?: PdfSignaturePlacementSnapshot | null;
      profileId: string;
      fileName?: string | null;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ job: PdfSigningJobInfo }>(
          `${apiBase}/signing-jobs`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          }
        );
        notify.success({ title: 'Signing started' });
        await Promise.all([signedVersions.mutate(), appearances.mutate()]);
        return result.job;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances, signedVersions]
  );

  const getSigningJob = useCallback(
    async (jobId: string) => {
      const result = await fetchJson<{ job: PdfSigningJobInfo }>(
        `${apiBase}/signing-jobs/${encodeURIComponent(jobId)}`
      );
      return result.job;
    },
    [apiBase]
  );

  const cancelSigningJob = useCallback(
    async (jobId: string) => {
      const result = await fetchJson<{ job: PdfSigningJobInfo }>(
        `${apiBase}/signing-jobs/${encodeURIComponent(jobId)}/cancel`,
        { method: 'POST' }
      );
      await signedVersions.mutate();
      return result.job;
    },
    [apiBase, signedVersions]
  );

  const appearanceImageUrl = useCallback(
    (appearanceId: string) =>
      `${apiBase}/appearances/${encodeURIComponent(appearanceId)}/file`,
    [apiBase]
  );

  const signedVersionDownloadUrl = useCallback(
    (versionId: string) =>
      `${apiBase}/signed-versions/${encodeURIComponent(versionId)}/download`,
    [apiBase]
  );

  return useMemo(
    () => ({
      workspaceId,
      appearances: appearances.data?.data.appearances ?? [],
      providers: providers.data?.data.providers ?? [],
      certificates: certificates.data?.data.certificates ?? [],
      profiles: profiles.data?.data.profiles ?? [],
      policy: policy.data?.data.policy ?? null,
      signedVersions: signedVersions.data?.data.versions ?? [],
      loadError:
        appearances.data?.error ??
        providers.data?.error ??
        certificates.data?.error ??
        profiles.data?.error ??
        policy.data?.error ??
        signedVersions.data?.error ??
        null,
      isLoading:
        enabled &&
        (!appearances.data ||
          !providers.data ||
          !certificates.data ||
          !profiles.data ||
          !policy.data),
      isSaving,
      refresh,
      uploadAppearance,
      createProvider,
      updateProvider,
      listAppearanceRoles,
      assignAppearanceRole,
      removeAppearanceRole,
      setDefaultAppearance,
      updateAppearance,
      deleteAppearance,
      createCertificate,
      updateCertificate,
      createSigningProfile,
      updateSigningProfile,
      deleteSigningProfile,
      setDefaultSigningProfile,
      testSigningProfile,
      updatePolicy,
      recordVisualExport,
      createSigningSourceSnapshot,
      requestSigning,
      getSigningJob,
      cancelSigningJob,
      appearanceImageUrl,
      signedVersionDownloadUrl,
    }),
    [
      appearanceImageUrl,
      assignAppearanceRole,
      appearances.data,
      certificates.data,
      cancelSigningJob,
      createSigningSourceSnapshot,
      createCertificate,
      createSigningProfile,
      createProvider,
      deleteSigningProfile,
      getSigningJob,
      isSaving,
      listAppearanceRoles,
      policy.data,
      profiles.data,
      recordVisualExport,
      providers.data,
      refresh,
      removeAppearanceRole,
      requestSigning,
      setDefaultAppearance,
      setDefaultSigningProfile,
      signedVersionDownloadUrl,
      signedVersions.data,
      testSigningProfile,
      deleteAppearance,
      updateCertificate,
      updateAppearance,
      updateProvider,
      updateSigningProfile,
      updatePolicy,
      uploadAppearance,
      workspaceId,
      enabled,
    ]
  );
}
